"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useDatePickerDialog = void 0;
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = require("react");
var _noop = require("../../../../constants/noop");
var _flyout = require("../../../flyout");
const defaultOffset = {
  distance: 4
};

/**
 * This component connects a DatePickerDialog and its target.
 * The target could be any focusable element.
 */
const useDatePickerDialog = options => {
  const {
    className,
    clientId,
    dayIsDisabled,
    id,
    initialPickerDate,
    isOpen,
    locale,
    maxDate,
    minDate,
    offset = defaultOffset,
    onChange: providedOnChange,
    onClear: providedOnClear,
    onCloseRequested: providedOnCloseRequested,
    onOpenRequested,
    pickerRef: providedPickerRef,
    placement = "bottom",
    refocusTargetOnClose = true,
    targetRef: providedTargetRef = null,
    value,
    ...rest
  } = options;
  const dialogProps = {
    className,
    clientId,
    dayIsDisabled,
    id,
    initialPickerDate,
    locale,
    maxDate,
    minDate,
    value
  };
  const pickerRef = (0, _lodestarUtils.useEnsuredForwardedRef)(providedPickerRef);
  const targetRef = (0, _lodestarUtils.useEnsuredForwardedRef)(providedTargetRef);
  const {
    targetProps,
    flyoutProps
  } = (0, _flyout.useFlyout)({
    isOpen,
    // We're going to ignore this for now. We'll deal with it manually in here.
    onCloseRequested: _noop.noop,
    placement,
    offset,
    // We need to handle this manually in the input since the element we need to
    // refocus is separate from the element we point at.
    closeOnEscape: false,
    id,
    targetRef,
    ...rest
  });
  const focusTarget = (0, _react.useCallback)(() => {
    requestAnimationFrame(() => {
      targetRef.current?.focus();
    });
  }, [targetRef]);

  /**
   * @param refocusTarget if true, manually focus the target again. We cannot rely on
   *                      Flyout here since we force the target ref to be the input.
   */
  const onCloseRequested = (0, _react.useCallback)(refocusTarget => {
    providedOnCloseRequested(refocusTarget);
    if (refocusTarget && refocusTargetOnClose) {
      focusTarget();
    }
  }, [providedOnCloseRequested, refocusTargetOnClose, focusTarget]);
  const onChange = (0, _react.useCallback)(newDate => {
    providedOnChange(newDate);
    if (refocusTargetOnClose) {
      focusTarget();
    }
  }, [providedOnChange, refocusTargetOnClose, focusTarget]);
  const onClear = (0, _react.useCallback)(() => {
    providedOnClear?.();
    if (refocusTargetOnClose) {
      focusTarget();
    }
  }, [providedOnClear, refocusTargetOnClose, focusTarget]);
  const onTargetClick = (0, _react.useCallback)(() => {
    if (!isOpen) {
      onOpenRequested();
    } else {
      onCloseRequested(true);
    }
  }, [isOpen, onCloseRequested, onOpenRequested]);
  const getPickerProps = overrides => {
    return {
      ...dialogProps,
      ...flyoutProps,
      ...overrides,
      onChange,
      onClear,
      // We're going to override the onCloseRequested from useFlyout here
      // since the picker dialogs need to do a little more work to focus
      // their calendar buttons.
      onCloseRequested,
      ref: pickerRef
    };
  };
  const getTargetHTMLProps = (0, _react.useCallback)(function () {
    let {
      onClick,
      ...overrides
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return {
      ...targetProps,
      ...overrides,
      onClick: (0, _lodestarUtils.callAllHandlers)(onClick, onTargetClick)
    };
  }, [onTargetClick, targetProps]);
  return {
    getPickerProps,
    getTargetHTMLProps
  };
};
exports.useDatePickerDialog = useDatePickerDialog;
//# sourceMappingURL=useDatePickerDialog.js.map